import { ToastMessage } from '@cokitchen/cokitchen-components';
import { CalendarIcon, DeleteIcon, EditAltIcon } from 'cokitchen-icons';
import { ReactComponent as Pin } from 'assets/icons/pin.svg';
import DeleteModal from 'components/base/delete-modal';
import { BlogType } from 'models/blog';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { formatDate, getWordsCount, handleApiError } from 'utils/functions';

const BlogCard = ({
  blog,
  refreshBlogs
}: {
  blog: BlogType;
  refreshBlogs: (id: string) => void;
}) => {
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeBlog, setActiveBlog] = useState('');
  const [wordsCount, setWordsCount] = useState(0);
  const blogUrl = `/create-blog/create-blog-body?blog_id=${blog.id}`;
  const disablePublish = useMemo(() => {
    return !blog.image || !blog.title || wordsCount < 100 || blog.tags.length === 0;
  }, [blog, wordsCount]);

  const html = React.useMemo(() => {
    const raw = blog.body || '';
    try {
      // body was saved as JSON.stringify(htmlString)
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') return parsed;
      return '';
    } catch {
      // fallback if body is already plain HTML string
      return raw;
    }
  }, [blog.body]);

  useEffect(() => {
    const wordCount = getWordsCount(blog.body);
    setWordsCount(wordCount);
  }, [blog.body]);

  const handleArchiveModal = async () => {
    setLoading(true);
    const url = !blog.archived ? `blog/${activeBlog}` : `blog/${activeBlog}/hard`;
    try {
      const res = await apiService.delete(url);
      if (res) {
        toast(<ToastMessage type='success' message={'Blog deleted successfully'} />);
        setShowArchiveModal(false);
        refreshBlogs(blog.id);
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };
  const onPublish = async () => {
    setLoading(true);
    try {
      const res = await apiService.patch(`blog/${blog.id}`, { data: { ...blog, is_draft: false } });
      if (res) {
        toast(<ToastMessage type='success' message={'Blog published successfully'} />);
        refreshBlogs(blog.id);
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };
  const onRestore = async () => {
    setLoading(true);
    try {
      const res = await apiService.patch(`blog/${blog.id}/restore`);
      if (res) {
        toast(<ToastMessage type='success' message={'Blog restored successfully'} />);
        refreshBlogs(blog.id);
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='relative border border-[#EDEDEE] pt-[5px] pb-4 rounded-[10px] flex flex-col justify-between min-w-[236px] px-3 h-full'>
      <div className=''>
        <img
          className='h-28 w-full object-cover rounded-t-[10px]'
          src={blog.image ?? ''}
          alt={blog.title}
        />
        <p className='font-medium text-[15px] mb-1 my-[10px]'>{blog.title}</p>
        <div
          className='prose max-w-full text-sm pb-2 text-[#96999C]'
          dangerouslySetInnerHTML={{ __html: html.slice(0, 145) }}
        />
      </div>
      <div className='flex justify-between py-3  text-xs text-gray-4 border-t border-[#EDEDEE]'>
        <div className='flex gap-1 items-center pl-3'>
          <CalendarIcon />
          <p>Added {formatDate(blog.created_at)}</p>
        </div>
      </div>
      <div className='flex justify-between gap-3'>
        <Link className='w-full' to={blogUrl}>
          <button className=' py-2 font-medium text-[#595959] border border-1 border-[#E8E8E8] rounded-[4px] text-sm w-full'>
            View details
          </button>
        </Link>

        {blog.is_draft && (
          <button
            disabled={disablePublish}
            onClick={onPublish}
            className={`w-full py-2 font-medium text-white  border border-1 border-[#E8E8E8] rounded-[4px] text-sm ${
              disablePublish ? 'opacity-80 cursor-not-allowed bg-[#a3a3a3] ' : 'bg-red-5'
            }`}>
            Publish
          </button>
        )}
        {blog.archived && (
          <button
            onClick={onRestore}
            className={`w-full py-2 font-medium text-white bg-red-5 border border-1 border-[#E8E8E8] rounded-[4px] text-sm `}>
            Restore
          </button>
        )}
      </div>
      {blog.pinned && (
        <div className='flex gap-2 absolute top-4 left-5 animate-pulse shadow-lg'>
          <span className='border border-gray-2 bg-white text-red-5 w-5 h-5 rounded-full p-0.5'>
            <Pin />
          </span>
        </div>
      )}

      <div className='flex gap-2 absolute top-4 right-6'>
        {!blog.archived && (
          <div className='bg-white text-[#858585] rounded-full p-1'>
            <Link to={blogUrl}>
              <EditAltIcon />
            </Link>
          </div>
        )}

        <div
          onClick={() => {
            setActiveBlog(blog.id);
            setShowArchiveModal(true);
          }}
          className='bg-white  text-[#858585]  rounded-full p-1'>
          <DeleteIcon />
        </div>
      </div>

      {showArchiveModal && activeBlog === blog.id && (
        <DeleteModal
          show={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          handleDelete={handleArchiveModal}
          description='This action cannot be undone'
          title={`Are you sure you want to ${blog.archived ? 'delete' : 'archive'} this blog?`}
          loading={loading}
        />
      )}
    </div>
  );
};

export default BlogCard;
